import type {
  CellFocusedEvent,
  ColDef,
  ColumnApi,
  ColumnEvent,
  ColumnState,
  GridApi,
  GridReadyEvent,
} from 'ag-grid-community'
import { BulkSheetView } from '../../../../containers/BulkSheetView'
import {
  defaultColDef as gridDefaultColDef,
  frameworkComponents,
} from '../../../../containers/commons/AgGrid'
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { VersionReportStatus } from '../../../../../domain/entity/version-report/VersionReportVersionEntity'
import { TeamEntity } from '../../../../../domain/entity/TeamEntity'
import styled from 'styled-components'

export type ProgressReportTableContent = {
  uuid: string
  name: string
  status: VersionReportStatus
  comment: string
  scheduledProgressRate: string
  progressRate: string
  total: string
  planned: string
  completed: string
  delay: string
  remaining: string
  wbsItemStatus: string
  team?: TeamEntity
  progressRateDiff: string
  scheduledStartDate?: string
  scheduledEndDate?: string
  maxChildScheduledEndDate?: string
  preceding: string
  unplanned: string
}
export type Value = {
  workload: number
  count: number
}

const columnEventValidSources = new Set([
  'toolPanelUi',
  'uiColumnMoved',
  'uiColumnResized',
  'uiColumnDragged',
])

type ProgressReportTableProps = {
  editing: boolean
  columns: ColDef[]
  contents: ProgressReportTableContent[]
  onGridReady?: (event: GridReadyEvent) => void
  updateColumnStateConfig?: (state: ColumnState[]) => void
  rowHeight?: number
  columnState?: ColumnState[]
}
export const ProgressReportTable = forwardRef<
  HTMLDivElement,
  ProgressReportTableProps
>(
  (
    {
      contents,
      editing,
      onGridReady,
      updateColumnStateConfig,
      rowHeight,
      columnState,
      columns,
    }: ProgressReportTableProps,
    ref
  ) => {
    const [gridInitialized, setGridInitialized] = useState(false)
    const gridApiRef = useRef<GridApi | null>(null)
    const columnApiRef = useRef<ColumnApi | null>(null)

    const defaultColDef = useMemo<ColDef>(
      () => gridDefaultColDef({ editable: false, sortable: true }),
      []
    )

    const onColumnChanged = useCallback(
      (e: ColumnEvent) => {
        if (!e.columnApi) return
        if (!e.source || !columnEventValidSources.has(e.source)) return
        const state = e.columnApi.getColumnState()
        updateColumnStateConfig?.(state)
      },
      [updateColumnStateConfig]
    )

    const applyStoredColumnState = useCallback(() => {
      if (columnApiRef.current && columnState && columnState.length > 0) {
        columnApiRef.current.applyColumnState({
          state: columnState,
          applyOrder: true,
        })
      }
    }, [columnState])
    const applyStoredColumnStateRef = useRef(applyStoredColumnState)
    useEffect(() => {
      applyStoredColumnStateRef.current = applyStoredColumnState
    }, [applyStoredColumnState])

    const handleGridReady = useCallback(
      (e: GridReadyEvent) => {
        gridApiRef.current = e.api
        columnApiRef.current = e.columnApi
        applyStoredColumnStateRef.current()
        onGridReady?.(e)
        // for avoiding flickering, set a delay before showing the grid
        setTimeout(() => setGridInitialized(true), 200)
      },
      [onGridReady]
    )

    const onCellFocused = useCallback((event: CellFocusedEvent) => {
      if (event.column) {
        event.api.ensureColumnVisible(event.column)
      }
    }, [])

    useEffect(() => {
      gridApiRef.current?.refreshHeader()
      applyStoredColumnStateRef.current()
    }, [editing])

    return (
      <HiddenUntilInitialized isVisible={gridInitialized}>
        <BulkSheetView
          ref={ref}
          rowData={contents}
          columnDefs={columns}
          defaultColDef={defaultColDef}
          rowHeight={rowHeight}
          components={frameworkComponents}
          onGridReady={handleGridReady}
          suppressContextMenu={true}
          maintainColumnOrder={true}
          suppressMovableColumns={!editing}
          suppressMoveWhenRowDragging={!editing}
          suppressDragLeaveHidesColumns={!editing}
          onColumnVisible={onColumnChanged}
          onColumnResized={onColumnChanged}
          onColumnMoved={onColumnChanged}
          enterMovesDown={true}
          enterMovesDownAfterEdit={true}
          onCellFocused={onCellFocused}
          {...(!editing ? { sideBar: undefined } : {})}
        />
      </HiddenUntilInitialized>
    )
  }
)

const HiddenUntilInitialized = styled('div')<{ isVisible: boolean }>(
  ({ isVisible }) => ({
    visibility: isVisible ? 'visible' : 'hidden',
    width: '100%',
    height: '100%',
  })
)
